import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook to check if the current user belongs to a system-tier organization
 * System admins have access to /internal routes and can create trial accounts
 */
export function useSystemAdmin() {
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    checkSystemAdmin();
  }, []);

  const checkSystemAdmin = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        setIsSystemAdmin(false);
        setLoading(false);
        return;
      }

      // Check if user has admin flag (user-level, not org-level)
      const { data: userData, error: dbError } = await supabase
        .from('users')
        .select('can_access_admin')
        .eq('id', user.id)
        .single();

      if (dbError) {
        console.error('Error checking system admin status:', dbError);
        setError(dbError.message);
        setIsSystemAdmin(false);
        setLoading(false);
        return;
      }

      setIsSystemAdmin(!!userData?.can_access_admin);
    } catch (err) {
      console.error('Unexpected error checking system admin:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      setIsSystemAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  return { isSystemAdmin, loading, error, refetch: checkSystemAdmin };
}

